import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";

import { CheckIcon, DropdownIcon } from "../icons";

export interface SelectMenuOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: SelectMenuOption[];
  placeholder: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  name?: string;
  labelId?: string;
  describedBy?: string;
}

// Dropdown with a styled option list. A native <select> renders its popup through the OS, so
// the design (check mark on the picked row, hover highlight, rounded panel) can only be met by
// drawing the list ourselves — hence the WAI-ARIA listbox pattern: a role="combobox" button
// owning a role="listbox" of role="option" rows, driven by aria-activedescendant.
export function SelectMenu({
  value,
  options,
  placeholder,
  onChange,
  onBlur,
  disabled,
  invalid,
  required,
  name,
  labelId,
  describedBy,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  // Row the keyboard is on. Kept in sync with the current value whenever the list opens.
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  useEffect(() => {
    if (!open) return;
    // Click anywhere outside (including on another dropdown) closes this one.
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Focus never leaves the button (aria-activedescendant pattern), so the browser won't
    // scroll the list for us: keep the active row inside the scrollable panel by hand.
    const active = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  const openList = () => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const pick = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActiveIndex((current) =>
          Math.min(Math.max(current + step, 0), Math.max(options.length - 1, 0))
        );
        return;
      }
      case "Home":
      case "End":
        if (!open) return;
        event.preventDefault();
        setActiveIndex(event.key === "Home" ? 0 : options.length - 1);
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        if (open) pick(activeIndex);
        else openList();
        return;
      case "Escape":
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        return;
      case "Tab":
        setOpen(false);
        return;
      default:
    }
  };

  return (
    <div className="select-menu" ref={rootRef}>
      <button
        type="button"
        className={`select-menu-button${selected ? "" : " is-placeholder"}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={labelId}
        aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        data-name={name}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      >
        <span className="select-menu-value">{selected ? selected.label : placeholder}</span>
        <DropdownIcon className="select-menu-icon" />
      </button>

      {open ? (
        <ul className="select-menu-list" id={listId} role="listbox" ref={listRef}>
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={`select-menu-option${index === activeIndex ? " is-active" : ""}`}
              onMouseEnter={() => setActiveIndex(index)}
              // Keep the focus on the button so its blur doesn't fire mid-click.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(index)}
            >
              {/* Decorative: aria-selected already carries "this is the picked row", and an
                  unlabelled glyph would otherwise leak into the option's accessible name. */}
              <span className="select-menu-check" aria-hidden="true">
                {option.value === value ? <CheckIcon width={16} height={16} /> : null}
              </span>
              <span>{option.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
